import React, { useContext } from 'react';
import styles from '../../PoliceReportDetail.styles.js';
import usePoliceReportDetailHook from './PoliceReportDetail.hook.js';
import { AppContext } from 'App.context.js';
import AppInput from 'Components/common/AppInput/AppInput.jsx';
import AppSelector from 'Components/common/AppSelector/AppSelector.jsx';
import AppInputTypes from 'Components/common/AppInput/AppInputTypes.js';
import AppButton from 'Components/common/Forms/AppButton/AppButton.jsx';
import AppModal from 'Components/common/AppModal/AppModal.jsx';
import moment from 'moment';
import DefaultLayout from 'Components/DefaultLayout/DefaultLayout.jsx';
import { useLocation } from 'react-router-dom';
import Colors from 'constants/Colors.js';
import PoliceReportSideMenuItem from '../../PoliceReportSideMenuItem/PoliceReportSideMenuItem.jsx';
import { SendOutlined, DeleteOutlined } from '@ant-design/icons';
import CustomButton from 'Components/common/CustomButton/CustomButton.jsx';
import Loading from 'Components/common/Loader/Loading.jsx';
import FIELDS from '../../../civitfun-police-report-utils/PoliceReportFields.js';

export default function PoliceReportDetail({ report }) {
  const {
    appActions: { translate },
  } = useContext(AppContext);
  const location = useLocation();
  const { state, actions } = usePoliceReportDetailHook(location?.state?.report);

  const getProvinceLabelForGuest = guest => {
    if (!guest) return '';

    if (guest[FIELDS.BIRTHPROVINCE_KEY]) {
      return actions.getProvince(guest[FIELDS.BIRTHPROVINCE_KEY])?.label || '';
    }

    if (guest[FIELDS.BIRTHCITY_KEY]) {
      return actions.getProvince(guest[FIELDS.BIRTHCITY_KEY])?.label || '';
    }

    return '';
  };

  return (
    <DefaultLayout howHotelSelector={false} singleBox>
      <div style={styles.wrapper}>
        <div style={styles.buttonsContainer}>
          <CustomButton
            text={translate('policeReport.back')}
            background={Colors.exportButton}
            color={Colors.white}
            onClick={() => {
              actions.onBackClicked();
            }}
          />
          {state.isLoading && <Loading size={'small'} />}
        </div>
        <div style={styles.screen}>
          <div style={{ ...styles.container, flex: 1 }}>
            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>{translate('policeReport.contractData')}</div>
              {state.report?.contract && (
                <div style={styles.inputContainer}>
                  <AppInput
                    style={styles.input}
                    title={translate('policeReport.detailFields.reference')}
                    value={state.report?.contract?.reference}
                    onChange={value => actions.updateContractField(FIELDS.REFERENCE_KEY, value)}
                    disabled={state.disableForm}
                    hasError={state.errorFields?.contract?.includes(FIELDS.REFERENCE_KEY)}
                  />
                  <AppInput
                    style={styles.input}
                    title={translate('policeReport.detailFields.date')}
                    value={
                      state.report?.contract?.[FIELDS.CONTRACTDATE_KEY]
                        ? moment(state.report?.contract?.[FIELDS.CONTRACTDATE_KEY])
                        : null
                    }
                    onChange={value => actions.updateContractField(FIELDS.CONTRACTDATE_KEY, value)}
                    type={AppInputTypes.DATE}
                    disabled={state.disableForm}
                    hasError={state.errorFields?.contract?.includes(FIELDS.CONTRACTDATE_KEY)}
                  />
                  <AppInput
                    style={styles.input}
                    title={translate('policeReport.detailFields.entrance')}
                    value={
                      state.report?.contract?.[FIELDS.ENTRANCE_KEY]
                        ? moment(state.report?.contract?.[FIELDS.ENTRANCE_KEY])
                        : null
                    }
                    onChange={value => actions.updateContractField(FIELDS.ENTRANCE_KEY, value)}
                    type={AppInputTypes.DATE}
                    disabled={state.disableForm}
                    hasError={state.errorFields?.contract?.includes(FIELDS.ENTRANCE_KEY)}
                    disabledDate={current => current && current > new Date()}
                  />
                  <AppInput
                    style={styles.input}
                    title={translate('policeReport.detailFields.departure')}
                    value={
                      state.report?.contract?.[FIELDS.DEPARTURE_KEY]
                        ? moment(state.report?.contract?.[FIELDS.DEPARTURE_KEY])
                        : null
                    }
                    onChange={value => actions.updateContractField(FIELDS.DEPARTURE_KEY, value)}
                    type={AppInputTypes.DATE}
                    disabled={state.disableForm}
                    hasError={state.errorFields?.contract?.includes(FIELDS.DEPARTURE_KEY)}
                    disabledDate={current => current && current > new Date()}
                  />
                  <AppInput
                    style={styles.input}
                    title={translate('policeReport.detailFields.guests')}
                    value={state.report?.contract?.[FIELDS.PAXES_KEY]}
                    onChange={value => actions.updateContractField(FIELDS.PAXES_KEY, value)}
                    type={AppInputTypes.NUMERIC}
                    disabled={state.disableForm}
                    hasError={state.errorFields?.contract?.includes(FIELDS.PAXES_KEY)}
                  />
                </div>
              )}
            </div>

            <div style={styles.separator} />
            {state.report?.guests?.map((guest, index) => (
              <>
                <div key={guest?.id || index} style={styles.separator} />
                <div style={styles.formSection}>
                  <div
                    style={styles.sectionTitle}
                  >{`${translate('policeReport.guestData')} ${index + 1}`}</div>
                  <div style={styles.inputContainer}>
                    <AppSelector
                      style={styles.input}
                      title={translate('policeReport.detailFields.role')}
                      value={guest?.[FIELDS.ROLETYPE_KEY]}
                      options={actions.getRoleTypeOptions()}
                      onOptionSelected={option =>
                        actions.updateGuestField(index, FIELDS.ROLETYPE_KEY, option)
                      }
                      isLoading={state.isLoading}
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.ROLETYPE_KEY)}
                    />
                    <AppInput
                      style={styles.input}
                      title={translate('policeReport.detailFields.name')}
                      value={guest?.[FIELDS.NAME_KEY]}
                      onChange={value => actions.updateGuestField(index, FIELDS.NAME_KEY, value)}
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.NAME_KEY)}
                    />
                    <AppInput
                      style={styles.input}
                      title={translate('policeReport.detailFields.surname')}
                      value={guest?.[FIELDS.SURNAME_KEY]}
                      onChange={value => actions.updateGuestField(index, FIELDS.SURNAME_KEY, value)}
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.SURNAME_KEY)}
                    />

                    <AppInput
                      style={styles.input}
                      title={translate('policeReport.detailFields.secondSurname')}
                      value={guest?.secondSurname}
                      onChange={value =>
                        actions.updateGuestField(index, FIELDS.SECONDSURNAME_KEY, value)
                      }
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(
                        FIELDS.SECONDSURNAME_KEY
                      )}
                    />
                    <AppSelector
                      style={styles.input}
                      title={translate('policeReport.detailFields.country')}
                      value={guest?.country || null}
                      options={actions.getCountryOptions()}
                      onOptionSelected={option =>
                        actions.updateGuestField(index, FIELDS.COUNTRY_KEY, option)
                      }
                      disabled={state.disableForm}
                      searchable
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.COUNTRY_KEY)}
                    />
                    <AppSelector
                      style={styles.input}
                      title={translate('policeReport.detailFields.gender')}
                      value={guest?.gender || null}
                      options={[
                        {
                          label: translate('policeReport.detailFields.genders.male'),
                          value: 'M',
                        },
                        {
                          label: translate('policeReport.detailFields.genders.female'),
                          value: 'F',
                        },
                        // { // only male female in alloggiati
                        //   label: translate('policeReport.detailFields.genders.other'),
                        //   value: 'U',
                        // },
                      ]}
                      onOptionSelected={option =>
                        actions.updateGuestField(index, FIELDS.GENDER_KEY, option)
                      }
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.GENDER_KEY)}
                    />
                  </div>

                  {/* Campos de nacimiento: fecha y país siempre visibles */}
                  <div style={styles.inputContainer}>
                    <AppInput
                      style={styles.input}
                      title={translate('policeReport.detailFields.birthDate')}
                      value={
                        guest[FIELDS.BIRTHDATE_KEY] ? moment(guest[FIELDS.BIRTHDATE_KEY]) : null
                      }
                      onChange={value =>
                        actions.updateGuestField(index, FIELDS.BIRTHDATE_KEY, value)
                      }
                      type={AppInputTypes.DATE}
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.BIRTHDATE_KEY)}
                      disabledDate={current => current && current > new Date()}
                    />
                    <AppSelector
                      style={styles.input}
                      title={translate('policeReport.detailFields.birthCountry')}
                      value={guest?.[FIELDS.BIRTHCOUNTRY_KEY]}
                      options={actions.getCountryOptions()} // <- devuelve ISO-3 con label localizado
                      onOptionSelected={option =>
                        actions.updateGuestField(index, FIELDS.BIRTHCOUNTRY_KEY, option)
                      }
                      isLoading={state.isLoading}
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.BIRTHCOUNTRY_KEY)}
                      searchable
                    />
                  </div>

                  {/* Si el país de nacimiento es Italia => Comune + Provincia */}
                  {guest?.[FIELDS.BIRTHCOUNTRY_KEY] === 'ITA' && (
                    <div style={styles.inputContainer}>
                      {/* Comune (selector con ISTAT 9) */}
                      <AppSelector
                        style={styles.input}
                        title={translate('policeReport.detailFields.birthCity')}
                        value={guest?.[FIELDS.BIRTHCITY_KEY] || null}
                        options={actions.getMunicipalityOptions()} // <- usa municipalities.json con meta.province
                        onOptionSelected={option =>
                          actions.updateGuestField(index, FIELDS.BIRTHCITY_KEY, option)
                        }
                        isLoading={state.isLoading}
                        disabled={state.disableForm}
                        hasError={state.errorFields?.guests[index]?.includes(FIELDS.BIRTHCITY_KEY)}
                        searchable
                      />
                      {/* Provincia (autocompletada y deshabilitada) */}

                      <AppInput
                        style={styles.input}
                        title={translate('policeReport.detailFields.birthProvince')}
                        value={getProvinceLabelForGuest(guest)}
                        disabled
                      />
                    </div>
                  )}

                  {/* only for holder guest index 0*/}
                  <div style={styles.inputContainer}>
                    <AppSelector
                      style={styles.input}
                      title={translate('policeReport.detailFields.documentType')}
                      value={guest?.[FIELDS.DOCUMENTTYPE_KEY] || null}
                      options={actions.getDocumentTypeOptions()}
                      onOptionSelected={option =>
                        actions.updateGuestField(index, FIELDS.DOCUMENTTYPE_KEY, option)
                      }
                      isLoading={state.isLoading}
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(FIELDS.DOCUMENTTYPE_KEY)}
                    />
                    <AppInput
                      style={styles.input}
                      title={translate('policeReport.detailFields.documentNumber')}
                      value={guest?.[FIELDS.DOCUMENTNUMBER_KEY]}
                      onChange={value =>
                        actions.updateGuestField(index, FIELDS.DOCUMENTNUMBER_KEY, value)
                      }
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(
                        FIELDS.DOCUMENTNUMBER_KEY
                      )}
                    />
                    <AppSelector
                      style={styles.input}
                      title={translate('policeReport.detailFields.documentIssuanceCountry')}
                      value={guest?.[FIELDS.DOCUMENTISSUANCECOUNTRY_KEY] || null}
                      options={actions.getCountryOptions()}
                      onOptionSelected={option =>
                        actions.updateGuestField(index, FIELDS.DOCUMENTISSUANCECOUNTRY_KEY, option)
                      }
                      disabled={state.disableForm}
                      hasError={state.errorFields?.guests[index]?.includes(
                        FIELDS.DOCUMENTISSUANCECOUNTRY_KEY
                      )}
                      searchable
                    />
                  </div>
                </div>
                {state.report?.guests.length > 1 && ( // dont show the button when there is only 1 guest
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '4%' }}>
                    <AppButton
                      action={() => actions.onDeleteGuestClicked(guest)}
                      redButton={true}
                      text={translate('policeReport.sideItem.delete.button')}
                    />
                  </div>
                )}
              </>
            ))}

            <AppModal
              show={state.showSendWarning}
              title={translate('policeReport.showSendWarningTitle')}
              description={translate('policeReport.showSendWarning')}
              okText={translate('Ok')}
              okAction={actions.sendReport}
              cancelAction={() => actions.setShowSendWarning(false)}
              closable={false}
            />
          </div>

          <div style={styles.sideMenu}>
            <PoliceReportSideMenuItem
              title={translate('policeReport.sideItem.booking.title')}
              indicatorColor={Colors.greenPill}
              header={translate('policeReport.sideItem.booking.header')}
              number={`${state.checkinCount}/${state.checkinCount}`}
              desc={translate('policeReport.sideItem.booking.desc')}
            />
            {state.disableForm ? null : (
              <>
                <PoliceReportSideMenuItem
                  icon={<SendOutlined style={{ fontSize: 30, marginBottom: 8 }} />}
                  header={translate('policeReport.sideItem.send.header')}
                  desc={translate('policeReport.sideItem.send.desc')}
                  action={actions.sendReport}
                >
                  <AppButton
                    action={() => actions.onSendClicked()}
                    text={translate('policeReport.sendData')}
                  />
                </PoliceReportSideMenuItem>
                <PoliceReportSideMenuItem
                  icon={<DeleteOutlined style={{ fontSize: 30, marginBottom: 8 }} />}
                  header={translate('policeReport.sideItem.delete.title')}
                  desc={translate('policeReport.sideItem.delete.desc')}
                >
                  <AppButton
                    action={() => actions.onDeleteBookingClicked()}
                    text={translate('policeReport.sideItem.delete.button')}
                    redButton={true}
                  />
                </PoliceReportSideMenuItem>
              </>
            )}
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
}
